import React from "react";
import { View, Pressable, StyleSheet } from "react-native";
import CustomText from "../../general/CustomText/CustomText";
import { colors, spacing, radii, typographyScale } from "../../../../styles/design-system";

export interface ChipOption {
  key: string;
  value: string;
  icon?: React.ReactNode;
}

export interface ChipSelectorProps {
  options: ChipOption[];
  selectedKeys: string[];
  onSelectionChange: (keys: string[]) => void;
  multiSelect?: boolean;
}

function ChipSelector({ options, selectedKeys, onSelectionChange, multiSelect = true }: ChipSelectorProps) {
  const handlePress = (key: string) => {
    if (multiSelect) {
      if (selectedKeys.includes(key)) {
        onSelectionChange(selectedKeys.filter((k) => k !== key));
      } else {
        onSelectionChange([...selectedKeys, key]);
      }
    } else {
      onSelectionChange([key]);
    }
  };

  return (
    <View style={styles.container}>
      {options.map((option) => {
        const isSelected = selectedKeys.includes(option.key);
        return (
          <Pressable
            key={option.key}
            onPress={() => handlePress(option.key)}
            style={({ pressed }) => [
              styles.chip,
              isSelected && styles.chipSelected,
              pressed && styles.chipPressed,
            ]}>
            {option.icon && <View style={styles.iconContainer}>{option.icon}</View>}
            <CustomText
              text={option.value}
              size="s"
              weight={isSelected ? "medium" : "regular"}
              style={[styles.chipText, isSelected && styles.chipTextSelected]}
              useTranslationText={false}
            />
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.full,
    borderWidth: 1,
    borderColor: colors.grey[300],
    backgroundColor: colors.grey[100],
    gap: spacing.xs,
  },
  chipSelected: {
    borderColor: colors.success[700],
    backgroundColor: colors.success[50],
  },
  chipPressed: {
    opacity: 0.7,
  },
  iconContainer: {
    width: 16,
    height: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  chipText: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  chipTextSelected: {
    color: colors.success[700],
  },
});

export default ChipSelector;
